import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Bell, MoreHorizontal, Check, Trash2, ExternalLink } from "lucide-react";
import { actorInitials, relativeTime, notificationLink } from "./notificationHelpers";

const MENU_WIDTH = 160;
const MENU_MAX_HEIGHT = 140;

// A single notification row. Used by both the header dropdown (compact) and the
// full notifications page (with a per-item "..." menu).
export default function NotificationItem({
  notification,
  compact = false,
  onActivate, // (notification) => void  — click/open
  onMarkRead, // (id) => void
  onDelete, // (id) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Position the portal menu relative to the trigger, flipping up near the
  // viewport bottom and clamping to stay on-screen.
  const computePosition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const openUp = rect.bottom + MENU_MAX_HEIGHT > window.innerHeight;
    const top = openUp ? rect.top - MENU_MAX_HEIGHT : rect.bottom + 4;
    let left = rect.right - MENU_WIDTH;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));
    setMenuPos({ top, left });
  }, []);

  const openMenu = (e) => {
    e.stopPropagation();
    if (!menuOpen) computePosition();
    setMenuOpen((v) => !v);
  };

  // Close on outside click, and on scroll/resize (fixed position would drift).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onDismiss = () => setMenuOpen(false);
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
    };
  }, [menuOpen]);

  const unread = !notification.is_read;
  const initials = actorInitials(notification.actor_name);
  // Only offer "Open" when the notification points somewhere (e.g. a project).
  // Types like "team member joined" have no destination, so Open is hidden.
  const hasLink = Boolean(notificationLink(notification));

  const runAction = (fn) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    fn && fn();
  };

  return (
    <div
      className={`relative flex items-start gap-3 px-4 py-3 border-b border-[#0000000D] cursor-pointer transition-colors hover:bg-[#FAFAFA] ${
        unread ? "bg-[#FBF4FA]" : "bg-white"
      }`}
      onClick={() => onActivate && onActivate(notification)}
    >
      {/* Unread accent bar */}
      {unread && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#A04A97] rounded-r" />
      )}

      {/* Actor avatar / system bell */}
      <div
        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${
          initials ? "bg-[#A04A97] text-white" : "bg-[#EFE3EE] text-[#A04A97]"
        }`}
      >
        {initials || <Bell size={16} />}
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        {notification.data?.saved_search_name && (
          <p className="font-outfit text-[13px] font-semibold uppercase tracking-[.04em] text-[#A04A97] truncate">
            {notification.data.saved_search_name}
          </p>
        )}
        <p
          className={`font-outfit text-[15px] leading-snug ${
            unread ? "text-[#27272A] font-medium" : "text-[#3F3F46]"
          } ${compact ? "line-clamp-2" : ""}`}
        >
          {notification.body}
        </p>
        <span className="block  text-[12px] text-[#767676]">
          {relativeTime(notification.created_at)}
        </span>
      </div>

      {/* Per-item menu (full page only) — rendered in a portal so it is never
          clipped by the list's overflow. */}
      {!compact && (
        <>
          <button
            ref={triggerRef}
            type="button"
            className="flex-shrink-0 p-1 rounded hover:bg-[#0000000D] text-[#71717A]"
            onClick={openMenu}
            aria-label="Notification actions"
          >
            <MoreHorizontal size={18} />
          </button>

          {menuOpen &&
            createPortal(
              <div
                ref={menuRef}
                style={{ position: "fixed", top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
                className="rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[10001] "
              >
                {hasLink && (
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13.5px] text-[#5A5A61] hover:bg-[#FAFAFA]"
                  onClick={runAction(() => onActivate && onActivate(notification))}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M1.55566 0.0498047H6.22266C6.43024 0.0499017 6.60207 0.119652 6.74121 0.258789C6.88035 0.397927 6.9501 0.56976 6.9502 0.777344C6.9502 0.985123 6.88045 1.15764 6.74121 1.29688C6.60208 1.43598 6.43021 1.50576 6.22266 1.50586H1.50586V12.4941H12.4941V7.77734C12.4942 7.56979 12.564 7.39792 12.7031 7.25879C12.8424 7.11955 13.0149 7.0498 13.2227 7.0498C13.4302 7.0499 13.6021 7.11965 13.7412 7.25879C13.8803 7.39793 13.9501 7.56976 13.9502 7.77734V12.4443C13.9502 12.8587 13.8031 13.2125 13.5078 13.5078C13.2125 13.8031 12.8587 13.9502 12.4443 13.9502H1.55566C1.14125 13.9502 0.787515 13.8031 0.492188 13.5078C0.19686 13.2125 0.0498047 12.8587 0.0498047 12.4443V1.55566C0.0498047 1.14125 0.19686 0.787515 0.492188 0.492188C0.787515 0.19686 1.14125 0.0498047 1.55566 0.0498047ZM9.33301 0.0498047H13.2227C13.4302 0.0499017 13.6021 0.119652 13.7412 0.258789C13.8803 0.397927 13.9501 0.56976 13.9502 0.777344V4.66699C13.9501 4.8746 13.8803 5.04639 13.7412 5.18555C13.6021 5.32468 13.4302 5.39443 13.2227 5.39453C13.0149 5.39453 12.8424 5.32478 12.7031 5.18555C12.564 5.0464 12.4942 4.87457 12.4941 4.66699V2.52344L12.4092 2.60938L5.71973 9.29785C5.58745 9.43004 5.41931 9.49707 5.21094 9.49707C5.00255 9.49703 4.83441 9.43011 4.70215 9.29785C4.56989 9.16559 4.50297 8.99745 4.50293 8.78906C4.50293 8.58069 4.56996 8.41255 4.70215 8.28027L11.3906 1.59082L11.4766 1.50586H9.33301C9.12543 1.50579 8.9536 1.43596 8.81445 1.29688C8.67522 1.15764 8.60547 0.985122 8.60547 0.777344C8.60557 0.56976 8.67532 0.397927 8.81445 0.258789C8.9536 0.119663 9.1254 0.0498776 9.33301 0.0498047Z" fill="#5A5A61" stroke="white" stroke-width="0.1"/>
</svg> Open
                </button>
                )}
                {!notification.is_read && (
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13.5px] text-[#5A5A61] hover:bg-[#FAFAFA]"
                    onClick={runAction(() => onMarkRead && onMarkRead(notification.id))}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
  <path d="M14.5168 7.54102C14.689 7.53824 14.8369 7.59843 14.9563 7.71973C15.0753 7.84044 15.135 7.98783 15.135 8.15918C15.135 8.33083 15.0755 8.47881 14.9563 8.59961L10.7053 12.9111C10.5593 13.0591 10.3868 13.1348 10.1897 13.1348C9.99276 13.1347 9.81998 13.059 9.67407 12.9111L7.57739 10.7842C7.46299 10.668 7.40516 10.5216 7.40259 10.3486C7.40003 10.1747 7.45814 10.0255 7.57739 9.9043C7.69652 9.78346 7.84313 9.72266 8.01294 9.72266C8.18232 9.72276 8.32757 9.78374 8.44653 9.9043L10.1897 11.6719L14.0872 7.71973C14.2017 7.60352 14.3458 7.5437 14.5168 7.54102ZM13.2258 0.0449219C13.6177 0.0450076 13.954 0.187138 14.2327 0.469727C14.5114 0.752518 14.6516 1.09406 14.6516 1.49121V5.58984C14.6516 5.76558 14.5924 5.91494 14.4739 6.03516C14.3552 6.15556 14.2073 6.21582 14.0334 6.21582C13.8594 6.21582 13.7115 6.15561 13.593 6.03516C13.4747 5.91495 13.4153 5.76553 13.4153 5.58984V2.45312L7.74536 6.13379C7.71697 6.15401 7.66927 6.17338 7.60767 6.19141C7.54407 6.21 7.4605 6.23002 7.35767 6.25098L7.3479 6.25293V6.25195C7.28035 6.25191 7.21298 6.2425 7.14673 6.22461V6.22363C7.07919 6.20547 7.01358 6.17678 6.95142 6.13574V6.13477L1.28149 2.45312V10.3145C1.28149 10.3736 1.29954 10.4188 1.33423 10.4541C1.36893 10.4893 1.41327 10.5077 1.47095 10.5078H5.53345C5.70725 10.5078 5.85534 10.5682 5.97388 10.6885C6.09254 10.8089 6.15161 10.9589 6.15161 11.1348C6.15157 11.3106 6.09251 11.46 5.97388 11.5801C5.8553 11.7004 5.70733 11.7607 5.53345 11.7607H1.47095C1.07913 11.7607 0.742766 11.6185 0.464111 11.3359C0.185343 11.0531 0.045166 10.7116 0.045166 10.3145V1.49121C0.045166 1.09406 0.185343 0.752518 0.464111 0.469727C0.742781 0.187142 1.0791 0.044998 1.47095 0.0449219H13.2258ZM7.3479 5.07324L13.1897 1.29785H1.5061L7.3479 5.07324Z" fill="#5A5A61" stroke="#5A5A61" stroke-width="0.09"/>
</svg> Mark as Read
                  </button>
                )}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13.5px] text-[#DC2626] hover:bg-[#FEF2F2]"
                  onClick={runAction(() => onDelete && onDelete(notification.id))}
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
}
